import { Component, inject } from '@angular/core';
import { HeaderComponent } from '@shared/layout/header/header.component';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ComponentsModule, SnackBarService } from '@mastechnicaegroup/carelinelive-ui';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { createPersonDetailsFormGroup } from '@lib/api/models/person';
import { Client, ClientSingleResponse, createClientDetailsFormGroup, StoreClientRequest } from '@lib/api/models/client';
import { SendableRequest } from '@mastechnicaegroup/carelinelive-cdk/requests';
import { createAddressFormGroup } from '@lib/api/models/address';
import { ClientService } from '@lib/client/client.service';
import { Router } from '@angular/router';
import { NgForOf } from '@angular/common';
import { createContactNumberFormGroup, StoreContactNumberRequest } from '@lib/api/models/contact-numbers';
import { FormValidationService } from '@shared/services/form-validation.service';

@Component({
    selector: 'app-clients-add-page',
    imports: [
        HeaderComponent,
        FormsModule,
        ReactiveFormsModule,
        ComponentsModule,
        ErrorPanelComponent,
        NgForOf,
    ],
    templateUrl: './clients-add-page.component.html',
    standalone: true,
    styleUrl: './clients-add-page.component.scss'
})
export class ClientsAddPageComponent {

    protected readonly addressFormGroup = createAddressFormGroup();
    private readonly _clientService = inject(ClientService);
    protected readonly clientDetailsFormGroup = createClientDetailsFormGroup();
    protected readonly personDetailsFormGroup = createPersonDetailsFormGroup();
    private readonly _snackBar = inject(SnackBarService);
    private readonly _router = inject(Router);
    private readonly _formValidation = inject(FormValidationService);
    private clientId = '';

    contactNumbersForm: FormGroup;

    createClientRequest = new SendableRequest<ClientSingleResponse, StoreClientRequest>({
        request: payload => this._clientService.createClient(payload!),

        onSend: () => {
            this.clientDetailsFormGroup.disable();
            this.personDetailsFormGroup.disable();
            this.addressFormGroup.disable();
        },

        onSuccess: response => {

            this.clientId = response.data.id;

            let contactNumbers = this.contactNumbersForm.value.contactNumbers;

            if (contactNumbers.length === 1) {
                if (contactNumbers[0].number === '' && contactNumbers[0].type === null) {
                    contactNumbers = [];
                }
            }

            this.addContactNumberToClient(response.data, contactNumbers);

            this._snackBar.open({
                colour: 'success',
                icon: 'check',
                title: 'Client added successfully.'
            });

            this._router.navigate(['/clients', response.data.id])
                .then(success => {
                    if (!success) {
                        this._snackBar.open({
                            colour: 'error',
                            icon: 'error',
                            title: 'Failed to navigate to the client.'
                        });
                        return;
                    }
                });
        },

        onError: () => {
            this.clientDetailsFormGroup.enable();
            this.personDetailsFormGroup.enable();
            this.addressFormGroup.enable();
        }
    });

    updateClientRequest = new SendableRequest<ClientSingleResponse, StoreClientRequest>({
        request: payload => this._clientService.updateClient(this.clientId, payload!),

        onSuccess: () => {
            return;
        }
    });

    constructor(private fb: FormBuilder) {
        this.contactNumbersForm = this.fb.group({
            contactNumbers: this.fb.array([createContactNumberFormGroup()])
        });
    }

    get contactNumbersFormArray(): FormArray {
        return this.contactNumbersForm.get('contactNumbers') as FormArray;
    }

    addContactNumberToClient(client: Client, contactNumbers: StoreContactNumberRequest[]) {
        const clientData: StoreClientRequest = {
            reference: client.reference,
            nhs_number: client.nhs_number,
            email: client.email,
            person: {
                title: client.person.title,
                given_name: client.person.given_name,
                initials: client.person.initials,
                family_name: client.person.family_name,
                middle_names: client.person.middle_names,
                preferred_name: client.person.preferred_name,
                birth_sex: client.person.birth_sex,
                pronouns: client.person.pronouns,
                date_of_birth: client.person.date_of_birth,
                additional_comments: client.person.additional_comments
            },
            contact_numbers_to_store: contactNumbers
        };

        this.updateClientRequest.send(clientData);
    }

    addContactNumber() {
        this.contactNumbersFormArray.push(createContactNumberFormGroup());
    }

    removeContactNumber(index: number) {
        this.contactNumbersFormArray.removeAt(index);

        if (this.contactNumbersFormArray.length === 0) {
            this.addContactNumber();
        }
    }


    add(): void {
        if (this.createClientRequest.loading()) {
            return;
        }

        if (!this.personDetailsFormGroup.valid || !this.clientDetailsFormGroup.valid || !this.addressFormGroup.valid) {
            this._formValidation.markFormGroupTouched(this.personDetailsFormGroup);
            this._formValidation.markFormGroupTouched(this.clientDetailsFormGroup);
            this._formValidation.markFormGroupTouched(this.addressFormGroup);
            
            this._formValidation.showFormErrors(this.personDetailsFormGroup, true);
            this._formValidation.showFormErrors(this.clientDetailsFormGroup, true);
            this._formValidation.showFormErrors(this.addressFormGroup, true);
            
            return;
        }

        const givenName = this.personDetailsFormGroup.value.given_name ?? '';
        const familyName = this.personDetailsFormGroup.value.family_name ?? '';
        const initials = (givenName.charAt(0) || '') + (familyName.charAt(0) || '');

        const clientData: StoreClientRequest = {
            reference: this.clientDetailsFormGroup.value.reference ?? '',
            email: this.clientDetailsFormGroup.value.email ?? '',
            nhs_number: this.clientDetailsFormGroup.value.nhs_number ?? '',
            person: {
                title: this.personDetailsFormGroup.value.title ?? '',
                given_name: givenName,
                middle_names: this.personDetailsFormGroup.value.middle_names ?? '',
                family_name: familyName,
                initials: initials,
                date_of_birth: this.personDetailsFormGroup.value.date_of_birth ?? '',
                preferred_name: this.personDetailsFormGroup.value.preferred_name ?? '',
                pronouns: this.personDetailsFormGroup.value.pronouns ?? '',
                birth_sex: this.personDetailsFormGroup.value.birth_sex ?? '',
                additional_comments: this.personDetailsFormGroup.value.additional_comments ?? '',
                address: {
                    street_1: this.addressFormGroup.value.street_1 ?? '',
                    street_2: this.addressFormGroup.value.street_2 ?? '',
                    town: this.addressFormGroup.value.town ?? '',
                    county: this.addressFormGroup.value.county ?? '',
                    country: this.addressFormGroup.value.country ?? '',
                    post_code: this.addressFormGroup.value.post_code ?? '',
                    hard_to_reach: this.addressFormGroup.value.hard_to_reach ?? false,
                    additional_comments: this.addressFormGroup.value.additional_comments ?? ''
                }
            }
        };

        this.createClientRequest.send(clientData);
    }

    reset(): void {
        this.addressFormGroup.reset();
        this.clientDetailsFormGroup.reset();
        this.contactNumbersForm.reset();
        this.personDetailsFormGroup.reset();
    }

    cancel(): void {
        this._router.navigate(['/clients'])
            .then(success => {
                if (!success) {
                    this._snackBar.open({
                        colour: 'error',
                        icon: 'error',
                        title: 'Failed to navigate to the client index page.'
                    });
                    return;
                }
            });

    }

    get dirtyFormCheck(): boolean {
        return (
          this.personDetailsFormGroup.dirty ||
          this.clientDetailsFormGroup.dirty ||
          this.addressFormGroup.dirty
        );
    }   
}
